/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { GegEvaluationResponse } from '../models/geg-evaluation-response';
import { GegEvaluationRequestPost } from '../models/geg-evaluation-request-post';
import { HeatDemandCalculationRequest } from '../models/heat-demand-calculation-request';
import { HeatDemandCalculationResponse } from '../models/heat-demand-calculation-response';
import { HeatLoadByBuildingHullRequest } from '../models/heat-load-by-building-hull-request';
import { HeatLoadByBuildingHullResponse } from '../models/heat-load-by-building-hull-response';
import { HeatLoadByConsumptionRequest } from '../models/heat-load-by-consumption-request';
import { HeatLoadByConsumptionResponse } from '../models/heat-load-by-consumption-response';
import { HeatLoadByCustomValueRequest } from '../models/heat-load-by-custom-value-request';
import { HeatLoadByCustomValueResponse } from '../models/heat-load-by-custom-value-response';
import { HeatLoadByEnergyCertRequest } from '../models/heat-load-by-energy-cert-request';
import { HeatLoadByEnergyCertResponse } from '../models/heat-load-by-energy-cert-response';
import { HeatLoadByLivingAreaRequest } from '../models/heat-load-by-living-area-request';
import { HeatLoadByLivingAreaResponse } from '../models/heat-load-by-living-area-response';
import { HeatLoadSelectRequest } from '../models/heat-load-select-request';
import { HeatLoadSelectResponse } from '../models/heat-load-select-response';
import { PartialHeatLoadsRequest } from '../models/partial-heat-loads-request';
import { PartialHeatLoadsResponse } from '../models/partial-heat-loads-response';


/**
 * Represents a special API resource for heat load calculations only.
 */
@Injectable({
  providedIn: 'root',
})
export class CalculationsControllerService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation calculationsControllerSelect
   */
  static readonly CalculationsControllerSelectPath = '/planning-projects/v1/calculations/heat-load/select';

  /**
   * Use this action to select a given heat load calculation method as active one and
   *      transfer related calculation input data from ViGuide Planning application-specific section into a project.
   *     <p>Please be aware of additional changes made by the API under the hood. After selection,
   *     the new selected method data located in the `viguidePlanningSpecificData.heatLoadCalculationsData` section
   *     are applied over to the project model data. All related data are overridden.</p>
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `calculationsControllerSelect()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  calculationsControllerSelect$Response(params: {
    body: HeatLoadSelectRequest
  },
  context?: HttpContext

): Observable<StrictHttpResponse<HeatLoadSelectResponse>> {

    const rb = new RequestBuilder(this.rootUrl, CalculationsControllerService.CalculationsControllerSelectPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<HeatLoadSelectResponse>;
      })
    );
  }

  /**
   * Use this action to select a given heat load calculation method as active one and
   *      transfer related calculation input data from ViGuide Planning application-specific section into a project.
   *     <p>Please be aware of additional changes made by the API under the hood. After selection,
   *     the new selected method data located in the `viguidePlanningSpecificData.heatLoadCalculationsData` section
   *     are applied over to the project model data. All related data are overridden.</p>
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `calculationsControllerSelect$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  calculationsControllerSelect(params: {
    body: HeatLoadSelectRequest
  },
  context?: HttpContext

): Observable<HeatLoadSelectResponse> {

    return this.calculationsControllerSelect$Response(params,context).pipe(
      map((r: StrictHttpResponse<HeatLoadSelectResponse>) => r.body as HeatLoadSelectResponse)
    );
  }

  /**
   * Path part for operation calculationsControllerEnergyCertificate
   */
  static readonly CalculationsControllerEnergyCertificatePath = '/planning-projects/v1/calculations/heat-load/energy-certificate';

  /**
   * Calculates heat load using Energy Certificate method.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `calculationsControllerEnergyCertificate()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  calculationsControllerEnergyCertificate$Response(params: {
    body: HeatLoadByEnergyCertRequest
  },
  context?: HttpContext

): Observable<StrictHttpResponse<HeatLoadByEnergyCertResponse>> {

    const rb = new RequestBuilder(this.rootUrl, CalculationsControllerService.CalculationsControllerEnergyCertificatePath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<HeatLoadByEnergyCertResponse>;
      })
    );
  }

  /**
   * Calculates heat load using Energy Certificate method.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `calculationsControllerEnergyCertificate$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  calculationsControllerEnergyCertificate(params: {
    body: HeatLoadByEnergyCertRequest
  },
  context?: HttpContext

): Observable<HeatLoadByEnergyCertResponse> {

    return this.calculationsControllerEnergyCertificate$Response(params,context).pipe(
      map((r: StrictHttpResponse<HeatLoadByEnergyCertResponse>) => r.body as HeatLoadByEnergyCertResponse)
    );
  }

  /**
   * Path part for operation calculationsControllerLivingArea
   */
  static readonly CalculationsControllerLivingAreaPath = '/planning-projects/v1/calculations/heat-load/living-area';

  /**
   * Defines a request body used to calculate heating load using Living Area method.<hr><h2>Request variants</h2><p><strong>NewBuilding</strong><br><code>{<br>&nbsp;&nbsp;&nbsp;&nbsp;"projectType": "NEW_BUILDING",<br>&nbsp;&nbsp;&nbsp;&nbsp;"livingArea": 220,<br>&nbsp;&nbsp;&nbsp;&nbsp;"projectCreationYear": 2020,<br>&nbsp;&nbsp;&nbsp;&nbsp;"numberOfPersons": 5,<br>&nbsp;&nbsp;&nbsp;&nbsp;"energyStandard": "KFW55",<br>&nbsp;&nbsp;&nbsp;&nbsp;"countryCode": "DE",<br>&nbsp;&nbsp;&nbsp;&nbsp;"postalCode": "35108"<br>}</code></p><p><strong>NewBuilding with optional <i>buildingPlacement</i></strong><br><code>{<br>&nbsp;&nbsp;&nbsp;&nbsp;"projectType": "NEW_BUILDING",<br>&nbsp;&nbsp;&nbsp;&nbsp;"livingArea": 220,<br>&nbsp;&nbsp;&nbsp;&nbsp;"projectCreationYear": 2020,<br>&nbsp;&nbsp;&nbsp;&nbsp;"numberOfPersons": 5,<br>&nbsp;&nbsp;&nbsp;&nbsp;"energyStandard": "KFW55",<br>&nbsp;&nbsp;&nbsp;&nbsp;"buildingPlacement": "CORNER",<br>&nbsp;&nbsp;&nbsp;&nbsp;"countryCode": "DE",<br>&nbsp;&nbsp;&nbsp;&nbsp;"postalCode": "35108"<br>}</code></p><p><strong>Modernization</strong><br><code>{<br>&nbsp;&nbsp;&nbsp;&nbsp;"projectType": "MODERNISATION",<br>&nbsp;&nbsp;&nbsp;&nbsp;"livingArea": 220,<br>&nbsp;&nbsp;&nbsp;&nbsp;"projectCreationYear": 2020,<br>&nbsp;&nbsp;&nbsp;&nbsp;"numberOfPersons": 5,<br>&nbsp;&nbsp;&nbsp;&nbsp;"countryCode": "DE",<br>&nbsp;&nbsp;&nbsp;&nbsp;"postalCode": "35108",<br>&nbsp;&nbsp;&nbsp;&nbsp;"ventilationHeatRecovery": true,<br>&nbsp;&nbsp;&nbsp;&nbsp;"buildingConstructionYear": 1998,<br>&nbsp;&nbsp;&nbsp;&nbsp;"roofModernizationYear": 2008,<br>&nbsp;&nbsp;&nbsp;&nbsp;"wallsModernizationYear": 2008,<br>&nbsp;&nbsp;&nbsp;&nbsp;"windowsModernizationYear": 2008<br>}</code></p><p><strong>Modernization with optional <i>buildingPlacement</i></strong><br><code>{<br>&nbsp;&nbsp;&nbsp;&nbsp;"projectType": "MODERNISATION",<br>&nbsp;&nbsp;&nbsp;&nbsp;"livingArea": 220,<br>&nbsp;&nbsp;&nbsp;&nbsp;"projectCreationYear": 2020,<br>&nbsp;&nbsp;&nbsp;&nbsp;"numberOfPersons": 5,<br>&nbsp;&nbsp;&nbsp;&nbsp;"countryCode": "DE",<br>&nbsp;&nbsp;&nbsp;&nbsp;"postalCode": "35108",<br>&nbsp;&nbsp;&nbsp;&nbsp;"ventilationHeatRecovery": true,<br>&nbsp;&nbsp;&nbsp;&nbsp;"buildingConstructionYear": 1998,<br>&nbsp;&nbsp;&nbsp;&nbsp;"roofModernizationYear": 2008,<br>&nbsp;&nbsp;&nbsp;&nbsp;"wallsModernizationYear": 2008,<br>&nbsp;&nbsp;&nbsp;&nbsp;"windowsModernizationYear": 2008,<br>&nbsp;&nbsp;&nbsp;&nbsp;"buildingPlacement": "CORNER"<br>}</code></p>
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `calculationsControllerLivingArea()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  calculationsControllerLivingArea$Response(params: {
    body: HeatLoadByLivingAreaRequest
  },
  context?: HttpContext

): Observable<StrictHttpResponse<HeatLoadByLivingAreaResponse>> {

    const rb = new RequestBuilder(this.rootUrl, CalculationsControllerService.CalculationsControllerLivingAreaPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<HeatLoadByLivingAreaResponse>;
      })
    );
  }

  /**
   * Defines a request body used to calculate heating load using Living Area method.<hr><h2>Request variants</h2><p><strong>NewBuilding</strong><br><code>{<br>&nbsp;&nbsp;&nbsp;&nbsp;"projectType": "NEW_BUILDING",<br>&nbsp;&nbsp;&nbsp;&nbsp;"livingArea": 220,<br>&nbsp;&nbsp;&nbsp;&nbsp;"projectCreationYear": 2020,<br>&nbsp;&nbsp;&nbsp;&nbsp;"numberOfPersons": 5,<br>&nbsp;&nbsp;&nbsp;&nbsp;"energyStandard": "KFW55",<br>&nbsp;&nbsp;&nbsp;&nbsp;"countryCode": "DE",<br>&nbsp;&nbsp;&nbsp;&nbsp;"postalCode": "35108"<br>}</code></p><p><strong>NewBuilding with optional <i>buildingPlacement</i></strong><br><code>{<br>&nbsp;&nbsp;&nbsp;&nbsp;"projectType": "NEW_BUILDING",<br>&nbsp;&nbsp;&nbsp;&nbsp;"livingArea": 220,<br>&nbsp;&nbsp;&nbsp;&nbsp;"projectCreationYear": 2020,<br>&nbsp;&nbsp;&nbsp;&nbsp;"numberOfPersons": 5,<br>&nbsp;&nbsp;&nbsp;&nbsp;"energyStandard": "KFW55",<br>&nbsp;&nbsp;&nbsp;&nbsp;"buildingPlacement": "CORNER",<br>&nbsp;&nbsp;&nbsp;&nbsp;"countryCode": "DE",<br>&nbsp;&nbsp;&nbsp;&nbsp;"postalCode": "35108"<br>}</code></p><p><strong>Modernization</strong><br><code>{<br>&nbsp;&nbsp;&nbsp;&nbsp;"projectType": "MODERNISATION",<br>&nbsp;&nbsp;&nbsp;&nbsp;"livingArea": 220,<br>&nbsp;&nbsp;&nbsp;&nbsp;"projectCreationYear": 2020,<br>&nbsp;&nbsp;&nbsp;&nbsp;"numberOfPersons": 5,<br>&nbsp;&nbsp;&nbsp;&nbsp;"countryCode": "DE",<br>&nbsp;&nbsp;&nbsp;&nbsp;"postalCode": "35108",<br>&nbsp;&nbsp;&nbsp;&nbsp;"ventilationHeatRecovery": true,<br>&nbsp;&nbsp;&nbsp;&nbsp;"buildingConstructionYear": 1998,<br>&nbsp;&nbsp;&nbsp;&nbsp;"roofModernizationYear": 2008,<br>&nbsp;&nbsp;&nbsp;&nbsp;"wallsModernizationYear": 2008,<br>&nbsp;&nbsp;&nbsp;&nbsp;"windowsModernizationYear": 2008<br>}</code></p><p><strong>Modernization with optional <i>buildingPlacement</i></strong><br><code>{<br>&nbsp;&nbsp;&nbsp;&nbsp;"projectType": "MODERNISATION",<br>&nbsp;&nbsp;&nbsp;&nbsp;"livingArea": 220,<br>&nbsp;&nbsp;&nbsp;&nbsp;"projectCreationYear": 2020,<br>&nbsp;&nbsp;&nbsp;&nbsp;"numberOfPersons": 5,<br>&nbsp;&nbsp;&nbsp;&nbsp;"countryCode": "DE",<br>&nbsp;&nbsp;&nbsp;&nbsp;"postalCode": "35108",<br>&nbsp;&nbsp;&nbsp;&nbsp;"ventilationHeatRecovery": true,<br>&nbsp;&nbsp;&nbsp;&nbsp;"buildingConstructionYear": 1998,<br>&nbsp;&nbsp;&nbsp;&nbsp;"roofModernizationYear": 2008,<br>&nbsp;&nbsp;&nbsp;&nbsp;"wallsModernizationYear": 2008,<br>&nbsp;&nbsp;&nbsp;&nbsp;"windowsModernizationYear": 2008,<br>&nbsp;&nbsp;&nbsp;&nbsp;"buildingPlacement": "CORNER"<br>}</code></p>
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `calculationsControllerLivingArea$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  calculationsControllerLivingArea(params: {
    body: HeatLoadByLivingAreaRequest
  },
  context?: HttpContext

): Observable<HeatLoadByLivingAreaResponse> {

    return this.calculationsControllerLivingArea$Response(params,context).pipe(
      map((r: StrictHttpResponse<HeatLoadByLivingAreaResponse>) => r.body as HeatLoadByLivingAreaResponse)
    );
  }

  /**
   * Path part for operation calculationsControllerBuildingHull
   */
  static readonly CalculationsControllerBuildingHullPath = '/planning-projects/v1/calculations/heat-load/building-hull';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `calculationsControllerBuildingHull()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  calculationsControllerBuildingHull$Response(params: {
    body: HeatLoadByBuildingHullRequest
  },
  context?: HttpContext

): Observable<StrictHttpResponse<HeatLoadByBuildingHullResponse>> {

    const rb = new RequestBuilder(this.rootUrl, CalculationsControllerService.CalculationsControllerBuildingHullPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<HeatLoadByBuildingHullResponse>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `calculationsControllerBuildingHull$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  calculationsControllerBuildingHull(params: {
    body: HeatLoadByBuildingHullRequest
  },
  context?: HttpContext

): Observable<HeatLoadByBuildingHullResponse> {

    return this.calculationsControllerBuildingHull$Response(params,context).pipe(
      map((r: StrictHttpResponse<HeatLoadByBuildingHullResponse>) => r.body as HeatLoadByBuildingHullResponse)
    );
  }

  /**
   * Path part for operation calculationsControllerConsumption
   */
  static readonly CalculationsControllerConsumptionPath = '/planning-projects/v1/calculations/heat-load/consumption';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `calculationsControllerConsumption()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  calculationsControllerConsumption$Response(params: {
    body: HeatLoadByConsumptionRequest
  },
  context?: HttpContext

): Observable<StrictHttpResponse<HeatLoadByConsumptionResponse>> {

    const rb = new RequestBuilder(this.rootUrl, CalculationsControllerService.CalculationsControllerConsumptionPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<HeatLoadByConsumptionResponse>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `calculationsControllerConsumption$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  calculationsControllerConsumption(params: {
    body: HeatLoadByConsumptionRequest
  },
  context?: HttpContext

): Observable<HeatLoadByConsumptionResponse> {

    return this.calculationsControllerConsumption$Response(params,context).pipe(
      map((r: StrictHttpResponse<HeatLoadByConsumptionResponse>) => r.body as HeatLoadByConsumptionResponse)
    );
  }

  /**
   * Path part for operation calculationsControllerCustomValue
   */
  static readonly CalculationsControllerCustomValuePath = '/planning-projects/v1/calculations/heat-load/custom-value';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `calculationsControllerCustomValue()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  calculationsControllerCustomValue$Response(params: {
    body: HeatLoadByCustomValueRequest
  },
  context?: HttpContext

): Observable<StrictHttpResponse<HeatLoadByCustomValueResponse>> {

    const rb = new RequestBuilder(this.rootUrl, CalculationsControllerService.CalculationsControllerCustomValuePath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<HeatLoadByCustomValueResponse>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `calculationsControllerCustomValue$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  calculationsControllerCustomValue(params: {
    body: HeatLoadByCustomValueRequest
  },
  context?: HttpContext

): Observable<HeatLoadByCustomValueResponse> {

    return this.calculationsControllerCustomValue$Response(params,context).pipe(
      map((r: StrictHttpResponse<HeatLoadByCustomValueResponse>) => r.body as HeatLoadByCustomValueResponse)
    );
  }

  /**
   * Path part for operation calculationsControllerPartialHeatLoads
   */
  static readonly CalculationsControllerPartialHeatLoadsPath = '/planning-projects/v1/calculations/partial-heat-loads';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `calculationsControllerPartialHeatLoads()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  calculationsControllerPartialHeatLoads$Response(params: {
    body: PartialHeatLoadsRequest
  },
  context?: HttpContext

): Observable<StrictHttpResponse<PartialHeatLoadsResponse>> {

    const rb = new RequestBuilder(this.rootUrl, CalculationsControllerService.CalculationsControllerPartialHeatLoadsPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<PartialHeatLoadsResponse>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `calculationsControllerPartialHeatLoads$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  calculationsControllerPartialHeatLoads(params: {
    body: PartialHeatLoadsRequest
  },
  context?: HttpContext

): Observable<PartialHeatLoadsResponse> {

    return this.calculationsControllerPartialHeatLoads$Response(params,context).pipe(
      map((r: StrictHttpResponse<PartialHeatLoadsResponse>) => r.body as PartialHeatLoadsResponse)
    );
  }

  /**
   * Path part for operation calculationsControllerHeatDemand
   */
  static readonly CalculationsControllerHeatDemandPath = '/planning-projects/v1/calculations/heat-demand';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `calculationsControllerHeatDemand()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  calculationsControllerHeatDemand$Response(params: {
    body: HeatDemandCalculationRequest
  },
  context?: HttpContext

): Observable<StrictHttpResponse<HeatDemandCalculationResponse>> {

    const rb = new RequestBuilder(this.rootUrl, CalculationsControllerService.CalculationsControllerHeatDemandPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<HeatDemandCalculationResponse>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `calculationsControllerHeatDemand$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  calculationsControllerHeatDemand(params: {
    body: HeatDemandCalculationRequest
  },
  context?: HttpContext

): Observable<HeatDemandCalculationResponse> {

    return this.calculationsControllerHeatDemand$Response(params,context).pipe(
      map((r: StrictHttpResponse<HeatDemandCalculationResponse>) => r.body as HeatDemandCalculationResponse)
    );
  }

  /**
   * Path part for operation calculationsControllerGegEvaluation
   */
  static readonly CalculationsControllerGegEvaluationPath = '/planning-projects/v1/calculations/geg-evaluation';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `calculationsControllerGegEvaluation()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  calculationsControllerGegEvaluation$Response(params: {
    body: GegEvaluationRequestPost
  },
  context?: HttpContext

): Observable<StrictHttpResponse<GegEvaluationResponse>> {

    const rb = new RequestBuilder(this.rootUrl, CalculationsControllerService.CalculationsControllerGegEvaluationPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<GegEvaluationResponse>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `calculationsControllerGegEvaluation$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  calculationsControllerGegEvaluation(params: {
    body: GegEvaluationRequestPost
  },
  context?: HttpContext

): Observable<GegEvaluationResponse> {

    return this.calculationsControllerGegEvaluation$Response(params,context).pipe(
      map((r: StrictHttpResponse<GegEvaluationResponse>) => r.body as GegEvaluationResponse)
    );
  }

}
